import { useState } from "react";
import { useEmployees } from "@/hooks/useEmployees";
import { useEmployeeAssets } from "@/hooks/useAssets";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingSpinnerWithText } from "@/components/ui/loading-spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Package, Eye, FileText, Calendar, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { EmployeeAssetsDialog } from "@/components/assets/EmployeeAssetsDialog";

export default function EmployeeAssets() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  const { employees = [], loading: employeesLoading } = useEmployees();
  const { data: allAssets = [], isLoading: assetsLoading } = useEmployeeAssets();
  
  const loading = employeesLoading || assetsLoading;
  
  // Filter employees with assets
  const employeesWithAssets = employees.filter(employee => {
    const hasAssets = allAssets.some(asset => 
      asset.employee_id === employee.id && asset.status === "assigned"
    );
    
    const matchesSearch = searchQuery === "" || 
      employee.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.department?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return hasAssets && matchesSearch;
  }).map(employee => {
    const employeeAssets = allAssets.filter(asset => 
      asset.employee_id === employee.id && asset.status === "assigned"
    );
    
    return {
      ...employee,
      assets: employeeAssets,
      totalAssets: employeeAssets.length,
      physicalAssets: employeeAssets.filter(a => a.asset_type === "physical").length,
      digitalAssets: employeeAssets.filter(a => a.asset_type === "digital").length,
      credentialsAssets: employeeAssets.filter(a => a.asset_type === "credentials").length,
      lastAssignedDate: employeeAssets.reduce((latest, asset) => {
        const assetDate = new Date(asset.assigned_date || 0);
        return assetDate > latest ? assetDate : latest;
      }, new Date(0))
    };
  });
  
  const handleViewDetails = (employee: any) => {
    setSelectedEmployee(employee);
    setIsDialogOpen(true);
  };
  
  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <LoadingSpinnerWithText text="جاري تحميل البيانات..." />
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-card-foreground">عهد الموظفين</h1>
          <p className="text-muted-foreground mt-1">عرض وإدارة العهد المسلمة للموظفين</p>
        </div>
      </div>
      
      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="البحث عن موظف..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10"
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Employees Grid */}
      {employeesWithAssets.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <User className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">لا يوجد موظفون لديهم عهد</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {employeesWithAssets.map((employee) => (
            <Card key={employee.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{employee.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      #{employee.id.slice(0, 8)}
                    </p>
                  </div>
                  <Badge variant="secondary">
                    {employee.totalAssets} عهدة
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">القسم</p>
                    <p className="font-medium">{employee.department || "غير محدد"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">المنصب</p>
                    <p className="font-medium">{employee.position || "غير محدد"}</p>
                  </div>
                </div>
                
                <div className="flex gap-2 justify-between text-sm">
                  <div className="flex items-center gap-1">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <span>{employee.physicalAssets} مادية</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span>{employee.digitalAssets} رقمية</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span>{employee.credentialsAssets} بيانات</span>
                  </div>
                </div>
                
                {employee.lastAssignedDate.getTime() > 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>
                      آخر تسليم: {format(employee.lastAssignedDate, "dd MMMM yyyy", { locale: ar })}
                    </span>
                  </div>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewDetails(employee)}
                  className="w-full"
                >
                  <Eye className="w-4 h-4 ml-2" />
                  عرض التفاصيل
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Employee Assets Dialog */}
      {selectedEmployee && (
        <EmployeeAssetsDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          employee={selectedEmployee}
        />
      )}
    </div>
  );
}